import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from '../i18n/translations';

type SupportedLanguage = 'nl' | 'en';

type TranslationDict = (typeof translations)[keyof typeof translations];

type LanguageContextValue = {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'site_language';

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<SupportedLanguage>('nl');

  useEffect(() => {
    const saved = (localStorage.getItem(STORAGE_KEY) as SupportedLanguage | null) || null;
    if (saved === 'nl' || saved === 'en') {
      setLanguage(saved);
      return;
    }
    const browserLang = navigator.language?.toLowerCase().startsWith('nl') ? 'nl' : 'en';
    setLanguage(browserLang);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const dict: TranslationDict = useMemo(() => translations[language], [language]);

  const t = useMemo(() => {
    const getter = (key: string): string => {
      const parts = key.split('.');
      let current: any = dict;
      for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          return key; // fallback to key if missing
        }
      }
      return typeof current === 'string' ? current : key;
    };
    return getter;
  }, [dict]);

  const value = useMemo(() => ({ language, setLanguage, t }), [language, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

export const useTranslation = () => {
  const { t } = useLanguage();
  return { t };
};


